import { useNurtureContext } from "../../../../Context/NurtureContext";
import { UpdatePostToCompletedService } from "../../../../Services/GetProspectsPosts";
import moment from "moment/moment";

import { Avatar, Box, Paper, Tooltip, Typography } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import { PiGlobeHemisphereWestFill } from "react-icons/pi";
import { BiRepost, BiMessageRoundedDetail } from "react-icons/bi";

import StyledButton from "../../ReusableComponents/Button";
import StyledCheckbox from "../../ReusableComponents/StyledCheckbox";
import Modal from "../../ReusableComponents/Modal";
import GenerateCommentModal from "./GenerateCommentModal";
import CookieErrDialog from "./CookieErrDialog";

import { useState } from "react";
import { mixpanelEvent } from "../../../../Utils/mixpanelUtils";
import { eligibleAgainLabel, queueTimeLabel } from "./autoCommentQueueLabels";

const styles = {
  Paper: {
    display: " flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    border: "1px solid #E0E2E4",
    borderRadius: "10px",
    paddingBlock: "16px",
    height: "100%",
    minHeight: "340px",
    position: "relative",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.06), 0px 0px 4px rgba(0, 0, 0, 0.04)",
  },
  Box: {
    paddingInline: "20px",
    display: "flex",
    alignItems: "flex-end",
  },
  Date: {
    fontFamily: "Inter",
    fontSize: "12px",
    fontWeight: "500",
    lineHeight: "9px",
    color: "#666666",
    whiteSpace: "nowrap",
  },
  Paragraph: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "20px",
    color: "#415058",
    display: "-webkit-box",
    "-webkit-line-clamp": "6",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    whiteSpace: "pre-line",
  },
  PostFooter: {
    gap: "1rem",
    padding: "20px 16px 0 16px",
  },
  Btn: {
    fontSize: "14px",
    padding: "8px 32px",
    height: "36px",
    borderRadius: "12px",
    "&:disabled": {
      color: "#000",
    },
  },
  engageBtn: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    background: "#F3F3F3",
    color: "#404040",

    "&:hover": {
      background: "#eee",
    },

    "& span": {
      fontFamily: "Inter",
      fontSize: "14px",
      fontWeight: "600",
      color: "#404040",
      lineHeight: "19px",
      whiteSpace: "nowrap",
    },

    "& svg": {
      width: "22px",
      height: "18px",
      flexShrink: 0,
    },
  },
  ProspectName: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "600",
    lineHeight: "10px",
    color: "#1F292E",
  },
  reshared: {
    border: "1px solid #415058",
    borderRadius: "200rem",
    padding: "6px 12px",
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: "14px",
    color: "#415058",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  newBadge: {
    background: "rgb(10, 114, 195, 0.1)",
    color: "var(--color-primary)",
    padding: "4px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1",
    marginLeft: "2px"
  },
  autoQueuedBadge: {
    background: "var(--color-grey-50)",
    color: "var(--color-grey-400)",
    border: "1px solid var(--color-grey-100)",
    padding: "4px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1",
    marginLeft: "2px"
  },
  autoPostedBadge: {
    background: "rgba(16, 185, 129, 0.18)",
    color: "#0B7A4F",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1.3",
    marginLeft: "2px"
  },
  autoDryRunBadge: {
    background: "rgba(16, 185, 129, 0.08)",
    color: "#0F9D66",
    border: "1px solid rgba(16, 185, 129, 0.35)",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1.3",
    marginLeft: "2px"
  },
  aiAssistedBadge: {
    background: "rgba(10, 114, 195, 0.12)",
    color: "var(--color-primary)",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1.3",
    marginLeft: "2px"
  },
  autoSkipBadge: {
    background: "var(--color-grey-50)",
    color: "var(--color-grey-400)",
    border: "1px solid var(--color-grey-100)",
    padding: "4px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "600",
    lineHeight: "1",
    marginLeft: "2px"
  },
  commentSection: {
    marginTop: "12px",
    padding: "10px 12px",
    background: "var(--color-grey-50)",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  commentSectionLabel: {
    fontFamily: "Inter",
    fontSize: "11px",
    fontWeight: "700",
    color: "var(--color-grey-400)",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  commentSectionTone: {
    fontFamily: "Inter",
    fontSize: "11px",
    fontWeight: "600",
    color: "var(--color-primary)",
    background: "rgba(10, 114, 195, 0.1)",
    padding: "1px 6px",
    borderRadius: "4px",
  },
  commentSectionText: {
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1F292E",
    whiteSpace: "pre-line",
  }
};

/** One-line content for the posted/dry-run badges - label and date joined with a separator,
 * each only when present. */
function AutoCommentBadgeContent({ label, completedAt }) {
  const parts = [label, completedAt ? moment(completedAt).format("MMM D, h:mm A") : null].filter(Boolean);
  return <>{parts.join(" · ")}</>;
}

function Post({ postData }) {
  const { setRefreshPostsKey } = useNurtureContext();

  const {
    "is complete": isComplete = false,
    "post summary": postSummary = "",
    post_content: postContent = "",
    "post date": postDate = "",
    "post link": postLink = "",
    first_name: firstName = "",
    last_name: lastName = "",
    id: postId,
    is_repost,
    is_reshare,
    profile_photo_url,
    generated_comment: generatedComment,
    auto_comment_status: autoCommentStatus,
    auto_comment_completed_at: autoCommentCompletedAt,
    auto_comment_message: autoCommentMessage,
    auto_generated: autoGenerated,
    auto_comment_tone_label: autoCommentToneLabel,
    auto_comment_estimated_at: autoCommentEstimatedAt,
    auto_comment_is_next: autoCommentIsNext,
    auto_comment_eligible_again_at: autoCommentEligibleAgainAt
  } = postData ?? {};
  // post_content is the actual scraped LinkedIn post text; post_summary is a separate
  // AI-generated summary that can drift from the original wording - prefer the real text,
  // falling back to the summary only for older rows that may not have post_content stored.
  const displayText = postContent.trim() || postSummary;

  const [completed, setCompleted] = useState(isComplete);
  const prospectName = `${firstName} ${lastName}`.trim();

  const newPosts = JSON.parse(localStorage.getItem("newPosts")) || [];
  const isNew = newPosts.some((post) => post.id === postLink);

  function daysPassed(dateString) {
    const date = moment(dateString);
    const now = moment();
    const diff = now.diff(date, "days");
    if (diff === 0) return "Today";
    if (diff >= 7) return `${Math.floor(diff / 7)}w`;

    return `${diff}d`;
  }

  const handleCompletePost = async () => {
    setCompleted(true);
    mixpanelEvent("post_marked", {
      name: prospectName,
      post_content: postSummary,
      screen_name: 'posting activity by monitored prospects', 
    });
    await UpdatePostToCompletedService(postId);
    setRefreshPostsKey((oldKey) => oldKey + 1);
    // Remove the post from newPosts if it is marked as read
    if (isNew) {
      const updatedPosts = newPosts.filter((post) => post.id !== postLink);
      localStorage.setItem("newPosts", JSON.stringify(updatedPosts));
    }
  };

  return (
    <Modal>
    <Paper sx={styles.Paper}>
      <Box sx={{ ...styles.Box, alignItems: "flex-start" }} justifyContent="space-between" mb="12px">
        <Box display="flex" gap="8px">
        <Avatar sx={{ height: "32px", width: "32px" }} alt="user" src={profile_photo_url}/>
          <Box display="flex" flexDirection="column" justifyContent="center" gap="8px">
            <Typography sx={styles.ProspectName}>{prospectName}</Typography>
            <Box display="flex" alignItems="center" gap="4px">
              <span style={styles.Date}>{daysPassed(postDate)}</span>
              <span style={{ color: "#666", lineHeight: "9px" }}>&bull;</span>
              <PiGlobeHemisphereWestFill style={{ color: "#666", height: "12px", width: "12px" }} />
              {isNew && (<span style={styles.newBadge}>New</span>)}
            </Box>
          </Box>
        </Box>
        <Tooltip title={!completed ? "Mark as read" : ""} placement="bottom-end">
          <StyledCheckbox checked={completed} disabled={completed} onClick={handleCompletePost} />
        </Tooltip>
      </Box>
      {(autoCommentStatus || generatedComment) && (
      <Box sx={{ ...styles.Box, flexDirection: "column", alignItems: "flex-start", gap: "6px" }} mb="16px">
        {autoCommentStatus === "queued" && (
          <span style={styles.autoQueuedBadge}>
            {[
              autoCommentIsNext ? "Next up for Agent Mode" : "Queued for Agent Mode",
              queueTimeLabel(autoCommentEstimatedAt, autoCommentIsNext),
            ].filter(Boolean).join(" · ")}
          </span>
        )}
        {autoCommentStatus === "posted" && (
          <span style={styles.autoPostedBadge}>
            <AutoCommentBadgeContent label="Agent Mode" completedAt={autoCommentCompletedAt} />
          </span>
        )}
        {autoCommentStatus === "dry_run" && (
          <span style={styles.autoDryRunBadge}>
            <AutoCommentBadgeContent label="Agent Mode (dry run)" completedAt={autoCommentCompletedAt} />
          </span>
        )}
        {autoCommentStatus === "ai_assisted" && (
          <span style={styles.aiAssistedBadge}>
            <AutoCommentBadgeContent label="AI Assisted" completedAt={autoCommentCompletedAt} />
          </span>
        )}
        {["repost", "cooldown", "too_old", "opened", "expires_before_turn"].includes(autoCommentStatus) && autoCommentMessage && (
          <span style={styles.autoSkipBadge}>
            {[autoCommentMessage, eligibleAgainLabel(autoCommentEligibleAgainAt)].filter(Boolean).join(" · ")}
          </span>
        )}
      </Box>
      )}
      {(is_repost || is_reshare) && (
        <Box sx={styles.Box}>
          <div style={styles.reshared}>
            <BiRepost style={{ height: "17px", width: "17px" }} />
            <span>{`${is_repost ? "Reposted" : "Reshared"} a post`}</span>
          </div>
        </Box>
      )}
      <Box sx={styles.Box}>
        <Typography sx={styles.Paragraph} variant="h6">
          {displayText}
        </Typography>
      </Box>
      {generatedComment && (
        <Box sx={{ ...styles.Box, marginTop: "auto" }}>
          <Box sx={styles.commentSection}>
            <Box display="flex" alignItems="center" gap="6px">
              <Typography sx={styles.commentSectionLabel}>Posted comment</Typography>
              {autoGenerated && autoCommentToneLabel && (
                <Typography sx={styles.commentSectionTone}>{autoCommentToneLabel}</Typography>
              )}
            </Box>
            <Typography sx={styles.commentSectionText}>{generatedComment}</Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ ...styles.Box, ...styles.PostFooter, marginTop: generatedComment ? 0 : "auto" }} display="flex">
        <a
          onClick={handleCompletePost}
          style={{ display: "inline-block", color: "white", textDecoration: "none", flex: 1 }}
          href={postLink}
          target="_blank"
          rel="noreferrer"
        >
          <StyledButton
            sx={styles.engageBtn}
            onClick={() => {
              mixpanelEvent("post_engaged", {
                post_id: postId,
                post_url: postLink,
                screen_name: 'posting activity by monitored prospects',
              });
            }}
          >
            <span>Open Post</span>
            <LaunchIcon />
          </StyledButton>
        </a>
        <Modal.Open
          opens="generateComment"
          onClick={() => {
            mixpanelEvent("ai_comment_modal_opened", {
              post_id: postId,
              post_url: postLink,
              screen_name: 'posting activity by monitored prospects',
            });
          }}
        >
          <StyledButton sx={{ ...styles.engageBtn, flex: 1 }} disabled={!!generatedComment}>
            <span>Engage</span>
            <BiMessageRoundedDetail />
          </StyledButton>
        </Modal.Open>
      </Box>
    </Paper>
    <Modal.Window
      name="generateComment"
      closeIcon
      modalStyles={{ width: "80vw", maxWidth: "960px" }}
      onClose={() => setRefreshPostsKey((oldKey) => oldKey + 1)}
    >
      <GenerateCommentModal postData={postData} />
    </Modal.Window>
    <Modal.Window name="cookieErr" closeIcon>
      <CookieErrDialog getInitialState={() => setRefreshPostsKey((oldKey) => oldKey + 1)} />
    </Modal.Window>
    </Modal>
  );
}

export default Post;
